import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  useToast,
} from '@biartbt/biartd'

type SortDir = 'asc' | 'desc'

export type CrudListResponse<TItem> = {
  items: TItem[]
  totalCount: number
  page: number
  pageSize: number
}

export type CrudSelectFilterOption = { label: string; value: string }

export type CrudSelectFilter = {
  id: string
  label: string
  value: string
  options: CrudSelectFilterOption[]
  widthClassName?: string
}

export type CrudColumn<TItem> = {
  key: string
  header: string
  sortable?: boolean
  render: (item: TItem) => React.ReactNode
  widthClassName?: string
}

type CrudMode = 'create' | 'edit'

export type CrudPageProps<TItem, TForm, TCreate, TUpdate> = {
  title: string
  subtitle?: string

  primaryActionLabel: string
  onPrimaryActionClick?: () => void
  onExportClick?: () => void
  onImportClick?: () => void
  onEditActionClick?: (item: TItem) => void
  /** Extra row actions (e.g. "Menü yönetimi" button next to Düzenle/Sil) */
  extraRowActions?: (item: TItem) => React.ReactNode

  // List
  list: (params: {
    search?: string
    page: number
    pageSize: number
    sortBy?: string
    sortDir?: SortDir
    filters: Record<string, string>
  }) => Promise<CrudListResponse<TItem>>

  getRowId: (item: TItem) => number | string

  // Table
  columns: CrudColumn<TItem>[]
  rowClickMode?: 'edit' | 'none'

  // Search + filters
  searchPlaceholder?: string
  filters?: CrudSelectFilter[]
  initialPageSize?: number

  // Form
  modalTitles: { create: string; edit: string }
  getCreateDefaultForm: () => TForm
  getEditForm: (item: TItem) => TForm
  validate: (form: TForm) => Record<string, string | undefined>
  validateWithMode?: (args: { mode: CrudMode; form: TForm; item: TItem | null }) => Record<string, string | undefined>
  renderForm: (args: {
    mode: CrudMode
    form: TForm
    setForm: React.Dispatch<React.SetStateAction<TForm>>
    touched: boolean
    setTouched: (t: boolean) => void
    errors: Record<string, string | undefined>
    isSubmitting: boolean
    item?: TItem | null
  }) => React.ReactNode

  buildCreatePayload: (form: TForm) => TCreate
  buildUpdatePayload: (form: TForm, item: TItem) => TUpdate

  // Mutations
  create: (payload: TCreate) => Promise<any>
  update: (id: number | string, payload: TUpdate) => Promise<any>
  remove: (id: number | string) => Promise<any>

  // Sync handling
  getCreatedId?: (createResult: any) => number | string | undefined
  isRowPresent?: (item: TItem, id: number | string) => boolean
  pollTimeoutMs?: number
  pollIntervalMs?: number
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

export function CrudPage<TItem, TForm, TCreate, TUpdate>({
  title,
  subtitle,
  primaryActionLabel,
  onPrimaryActionClick,
  onExportClick,
  onImportClick,
  onEditActionClick,
  extraRowActions,
  list,
  getRowId,
  columns,
  rowClickMode = 'edit',
  searchPlaceholder = 'Search...',
  filters = [],
  initialPageSize = 10,
  modalTitles,
  getCreateDefaultForm,
  getEditForm,
  validate,
  validateWithMode,
  renderForm,
  buildCreatePayload,
  buildUpdatePayload,
  create,
  update,
  remove,
  getCreatedId,
  isRowPresent,
  pollTimeoutMs = 5000,
  pollIntervalMs = 500,
}: CrudPageProps<TItem, TForm, TCreate, TUpdate>) {
  const { toast } = useToast()

  const [page, setPage] = useState(1)
  const [pageSize] = useState(initialPageSize)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const [filterState, setFilterState] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const f of filters) initial[f.id] = f.value
    return initial
  })

  // Debounced search
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 350)
    return () => window.clearTimeout(t)
  }, [searchInput])

  const listParams = useMemo(
    () => ({
      search: search?.trim() ? search.trim() : undefined,
      page,
      pageSize,
      sortBy,
      sortDir,
      filters: filterState,
    }),
    [search, page, pageSize, sortBy, sortDir, filterState],
  )
  const listParamsJson = JSON.stringify(listParams)

  const [data, setData] = useState<CrudListResponse<TItem> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<any>(null)

  const fetchList = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false
    setError(null)
    setIsFetching(true)
    if (!silent) setIsLoading(true)
    try {
      const res = await list(listParams)
      setData(res)
    } catch (e) {
      setError(e)
    } finally {
      setIsFetching(false)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParamsJson])

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const [modalOpen, setModalOpen] = useState(false)
  const [mode, setMode] = useState<CrudMode>('create')
  const [selected, setSelected] = useState<TItem | null>(null)
  const [form, setForm] = useState<TForm>(() => getCreateDefaultForm())
  const [touched, setTouched] = useState(false)

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<TItem | null>(null)

  const errors = validateWithMode ? validateWithMode({ mode, form, item: selected }) : validate(form)
  const hasErrors = Object.values(errors).some(Boolean)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const openCreate = () => {
    setMode('create')
    setSelected(null)
    setTouched(false)
    setForm(getCreateDefaultForm())
    setModalOpen(true)
  }

  const openEdit = (item: TItem) => {
    setMode('edit')
    setSelected(item)
    setTouched(false)
    setForm(getEditForm(item))
    setModalOpen(true)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(key)
    setSortDir('asc')
  }

  const pollUntil = async (predicate: (items: TItem[]) => boolean) => {
    const started = Date.now()
    while (Date.now() - started < pollTimeoutMs) {
      try {
        const res = await list(listParams)
        setData(res)
        if (predicate(res.items)) return true
      } catch {
        // ignore during polling
      }
      await sleep(pollIntervalMs)
    }
    return false
  }

  const submit = async () => {
    setTouched(true)
    if (hasErrors) return

    setIsSubmitting(true)
    try {
      if (mode === 'create') {
        const res = await create(buildCreatePayload(form))
        toast({ title: 'Kaydedildi', description: 'Kaydedildi, senkronize ediliyor...', variant: 'info', durationMs: 2500 })

        const createdId = getCreatedId?.(res)
        await fetchList({ silent: true })

        if (createdId !== undefined) {
          const ok = await pollUntil((rows) =>
            rows.some((r) => {
              const rid = getRowId(r)
              return isRowPresent ? isRowPresent(r, createdId) : String(rid) === String(createdId)
            }),
          )
          if (!ok) {
            toast({ title: 'Kaydedildi', description: "Senkron bekleniyor. Yenile'ye tıkla.", variant: 'warning' })
          } else {
            toast({ title: 'Başarılı', description: 'Kayıt oluşturuldu.', variant: 'success' })
          }
        } else {
          toast({ title: 'Başarılı', description: 'Kayıt oluşturuldu.', variant: 'success' })
        }
      } else if (mode === 'edit' && selected) {
        const id = getRowId(selected)
        await update(id, buildUpdatePayload(form, selected))
        toast({ title: 'Kaydedildi', description: 'Kaydedildi, senkronize ediliyor...', variant: 'info', durationMs: 2500 })
        await fetchList({ silent: true })
        const ok = await pollUntil((rows) => rows.some((r) => String(getRowId(r)) === String(id)))
        if (!ok) toast({ title: 'Kaydedildi', description: "Senkron bekleniyor. Yenile'ye tıkla.", variant: 'warning' })
        else toast({ title: 'Başarılı', description: 'Kayıt güncellendi.', variant: 'success' })
      }

      setModalOpen(false)
    } catch (e: any) {
      toast({ title: 'Hata', description: e?.message ?? 'İşlem başarısız.', variant: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const requestDelete = (item: TItem) => {
    setDeleteTarget(item)
    setConfirmDeleteOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const id = getRowId(deleteTarget)
    setIsDeleting(true)
    try {
      await remove(id)
      toast({ title: 'Silindi', description: 'Silindi, senkronize ediliyor...', variant: 'info', durationMs: 2500 })
      await fetchList({ silent: true })

      const ok = await pollUntil((rows) => rows.every((r) => String(getRowId(r)) !== String(id)))
      if (!ok) toast({ title: 'Silindi', description: "Senkron bekleniyor. Yenile'ye tıkla.", variant: 'warning' })
      else toast({ title: 'Başarılı', description: 'Kayıt silindi.', variant: 'success' })
    } catch (e: any) {
      toast({ title: 'Hata', description: e?.message ?? 'Silme başarısız.', variant: 'danger' })
    } finally {
      setIsDeleting(false)
      setConfirmDeleteOpen(false)
      setDeleteTarget(null)
    }
  }

  return (
    <div className="p-[25px]">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {onExportClick && (
              <Button variant="outline" onClick={onExportClick} disabled={isFetching || isLoading}>
                Excel'e Aktar
              </Button>
            )}
            {onImportClick && (
              <Button variant="outline" onClick={onImportClick} disabled={isFetching || isLoading}>
                Excel'den İçe Aktar
              </Button>
            )}
            <Button variant="outline" onClick={() => fetchList()} disabled={isFetching || isLoading}>
              Yenile
            </Button>
            <Button
              leftIcon={<i className="material-symbols-outlined">add</i>}
              onClick={onPrimaryActionClick ?? openCreate}
              disabled={isLoading}
            >
              {primaryActionLabel}
            </Button>
          </>
        }
      />

      {error && (
        <div className="mt-4">
          <Alert variant="danger" type="default">
            {error?.message ?? 'Liste yüklenemedi.'}
          </Alert>
        </div>
      )}

      <Card className="mt-4">
        <TableToolbar
          className="mb-4"
          left={
            <>
              <div className="min-w-[260px]">
                <Input
                  aria-label="Search"
                  placeholder={searchPlaceholder}
                  value={searchInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      setSearch(searchInput)
                      setPage(1)
                    }
                  }}
                  leftIcon={<i className="material-symbols-outlined text-lg">search</i>}
                />
              </div>
              {filters.map((f) => (
                <div key={f.id} className={f.widthClassName ?? 'min-w-[180px]'}>
                  <Select
                    value={filterState[f.id] ?? ''}
                    onValueChange={(v: string) => {
                      setFilterState((s) => ({ ...s, [f.id]: v }))
                      setPage(1)
                    }}
                  >
                    <SelectTrigger aria-label={f.label}>
                      <SelectValue placeholder={f.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </>
          }
          right={
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Toplam: <span className="font-medium text-gray-900 dark:text-gray-100">{totalCount}</span>
            </div>
          }
        />

        {isLoading ? (
          <div className="grid gap-3">
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Kayıt bulunamadı"
            description="Arama/filtreleri değiştir veya yeni kayıt oluştur."
            action={<Button onClick={openCreate}>{primaryActionLabel}</Button>}
            icon={<i className="material-symbols-outlined text-3xl">inventory_2</i>}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.key} className={c.widthClassName}>
                      {c.sortable ? (
                        <Button variant="ghost" size="sm" onClick={() => toggleSort(c.key)}>
                          {c.header} {sortBy === c.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                      ) : (
                        c.header
                      )}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={String(getRowId(item))}
                    className={rowClickMode === 'edit' ? 'cursor-pointer' : undefined}
                    onClick={() => rowClickMode === 'edit' && openEdit(item)}
                  >
                    {columns.map((c) => (
                      <TableCell key={c.key} className={c.widthClassName}>
                        {c.render(item)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                        {extraRowActions?.(item)}
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); if (onEditActionClick) { onEditActionClick(item); return }; openEdit(item) }}>
                          Düzenle
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); requestDelete(item) }}
                        >
                          Sil
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-end mt-[12px]">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} disabled={isFetching} />
            </div>
          </>
        )}
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          size="2xl"
          className="p-[20px] md:p-[25px] flex flex-col max-h-[85vh]"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? modalTitles.create : modalTitles.edit}</DialogTitle>
          </DialogHeader>

          {/* Scrollable form area (mobile-first) */}
          <div className="flex-1 overflow-y-auto pr-1 -mr-1">
            {renderForm({
              mode,
              form,
              setForm,
              touched,
              setTouched,
              errors,
              isSubmitting,
              item: mode === 'edit' ? selected : null,
            })}
          </div>

          {/* Sticky-style footer (always visible) */}
          <DialogFooter className="mt-4 pt-4 border-t trezo-border-color flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              disabled={isSubmitting}
              className="w-full min-h-[44px] sm:w-auto"
            >
              İptal
            </Button>
            <Button
              onClick={submit}
              loading={isSubmitting}
              disabled={hasErrors || isSubmitting}
              className="w-full min-h-[44px] sm:w-auto"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Silme Onayı"
        description="Bu kaydı silmek istediğinize emin misiniz?"
        confirmLabel="Sil"
        cancelLabel="Vazgeç"
        confirmVariant="destructive"
        loading={isDeleting}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

